import React, { useEffect, useState } from 'react';
import { useTranslation } from 'react-i18next';
import { useNavigate } from 'react-router-dom';
import { Link2, ChefHat, Layers, EyeOff, CheckCircle2 } from 'lucide-react';
import { getInventoryCoverage } from '../services/api';
import { normalizeCoverage, markProductCovered } from '../utils/coverage';
import { trackEvent } from '../utils/analytics';
import ResaleLinkModal from './ResaleLinkModal';
import './InventoryCoveragePanel.css';

/** Cuántos pendientes se muestran antes de "Ver todos". */
const VISIBLE_PENDING = 8;

/**
 * Asistente de cobertura (RES-39): cuántos productos de la carta ya descuentan stock y cuáles
 * todavía no, ordenados por ventas de los últimos 30 días (los que quedaron "para después" en el
 * wizard, primero). Desde cada fila se vincula como reventa o se va a armar la receta.
 * @param {{canEdit?: boolean, catalogItems?: Object[], onLinked?: Function}} props - Props
 * @returns {JSX.Element|null} Panel
 */
function InventoryCoveragePanel({ canEdit = true, catalogItems = [], onLinked }) {
  const { t } = useTranslation();
  const navigate = useNavigate();
  const [coverage, setCoverage] = useState(null);
  const [linking, setLinking] = useState(null);
  const [showAll, setShowAll] = useState(false);
  const [notice, setNotice] = useState('');

  useEffect(() => {
    let cancelled = false;
    getInventoryCoverage()
      .then((data) => {
        if (cancelled) return;
        const normalized = normalizeCoverage(data);
        setCoverage(normalized);
        trackEvent('inventory_coverage_viewed', {
          coverage_percent: normalized.coveragePercent,
          pending_count: normalized.pendingCount,
        });
      })
      .catch((error) => {
        // Sin cobertura (permiso o módulo apagado) la página de inventario sigue andando
        console.error('Error loading inventory coverage:', error);
      });
    return () => {
      cancelled = true;
    };
  }, []);

  if (!coverage) return null;
  const hasOthers = coverage.combos.length > 0 || coverage.unavailable.length > 0;
  if (coverage.totalProducts === 0 && !hasOthers) return null;

  const pending = showAll ? coverage.pending : coverage.pending.slice(0, VISIBLE_PENDING);

  const handleLinked = ({ product, inventoryLink, createdItem }) => {
    setLinking(null);
    setCoverage((prev) => markProductCovered(prev, product.itemId, 'LINK'));
    setNotice(t('inventory.coverage.linked', { name: product.name }));
    if (onLinked) onLinked({ product, inventoryLink, createdItem });
  };

  const openRecipe = (product) =>
    navigate(`/menu/items/edit/${product.itemId}`, {
      state: { categoryId: product.categoryId || '', focusSection: 'recipe' },
    });

  return (
    <section className="card coverage-panel" aria-labelledby="coverage-panel-title">
      <div className="coverage-header">
        <div>
          <h2 id="coverage-panel-title">{t('inventory.coverage.title')}</h2>
          <p className="coverage-summary">
            {t('inventory.coverage.summary', {
              covered: coverage.coveredCount,
              total: coverage.totalProducts,
            })}
          </p>
        </div>
        <strong className="coverage-percent">{coverage.coveragePercent}%</strong>
      </div>
      <div
        className="coverage-progress"
        role="progressbar"
        aria-valuemin={0}
        aria-valuemax={100}
        aria-valuenow={coverage.coveragePercent}
        aria-label={t('inventory.coverage.title')}
      >
        <div className="coverage-progress-bar" style={{ width: `${coverage.coveragePercent}%` }} />
      </div>

      {!coverage.salesAvailable && (
        <p className="coverage-note">{t('inventory.coverage.salesUnavailable')}</p>
      )}

      {notice && (
        <p className="coverage-notice" role="status" aria-live="polite">
          <CheckCircle2 size={16} aria-hidden="true" />
          {notice}
        </p>
      )}

      {coverage.pending.length === 0 ? (
        coverage.totalProducts > 0 && (
          <p className="coverage-done">
            <CheckCircle2 size={18} aria-hidden="true" />
            {t('inventory.coverage.allCovered')}
          </p>
        )
      ) : (
        <>
          <h3 className="coverage-subtitle">
            {t('inventory.coverage.pendingTitle', { count: coverage.pendingCount })}
          </h3>
          <ul className="coverage-list">
            {pending.map((product) => (
              <li key={product.itemId} className="coverage-row" data-item-id={product.itemId}>
                <div className="coverage-row-info">
                  <span className="coverage-row-name">{product.name}</span>
                  {product.deferred && (
                    <span className="badge badge-warning coverage-deferred">
                      {t('inventory.coverage.deferredBadge')}
                    </span>
                  )}
                  {coverage.salesAvailable && (
                    <span className="coverage-row-sales">
                      {t('inventory.coverage.unitsSold', {
                        count: product.unitsSold || 0,
                        days: coverage.periodDays,
                      })}
                    </span>
                  )}
                </div>
                {canEdit && (
                  <div className="coverage-row-actions">
                    <button
                      type="button"
                      className="btn btn-secondary btn-sm coverage-link-btn"
                      onClick={() => {
                        setNotice('');
                        setLinking(product);
                      }}
                    >
                      <Link2 size={14} aria-hidden="true" />
                      {t('inventory.coverage.linkResale')}
                    </button>
                    <button
                      type="button"
                      className="btn btn-ghost btn-sm coverage-recipe-btn"
                      onClick={() => openRecipe(product)}
                    >
                      <ChefHat size={14} aria-hidden="true" />
                      {t('inventory.coverage.buildRecipe')}
                    </button>
                  </div>
                )}
              </li>
            ))}
          </ul>
          {coverage.pending.length > VISIBLE_PENDING && (
            <button
              type="button"
              className="btn btn-ghost btn-sm coverage-toggle"
              onClick={() => setShowAll((prev) => !prev)}
            >
              {showAll
                ? t('inventory.coverage.showLess')
                : t('inventory.coverage.showAll', { count: coverage.pending.length })}
            </button>
          )}
        </>
      )}

      {coverage.combos.length > 0 && (
        <details className="coverage-extra">
          <summary>
            <Layers size={16} aria-hidden="true" />
            {t('inventory.coverage.combosTitle', { count: coverage.combos.length })}
          </summary>
          <p className="coverage-note">{t('inventory.coverage.combosHelp')}</p>
          <ul className="coverage-extra-list">
            {coverage.combos.map((product) => (
              <li key={product.itemId}>{product.name}</li>
            ))}
          </ul>
        </details>
      )}

      {coverage.unavailable.length > 0 && (
        <details className="coverage-extra">
          <summary>
            <EyeOff size={16} aria-hidden="true" />
            {t('inventory.coverage.unavailableTitle', { count: coverage.unavailable.length })}
          </summary>
          <p className="coverage-note">{t('inventory.coverage.unavailableHelp')}</p>
          <ul className="coverage-extra-list">
            {coverage.unavailable.map((product) => (
              <li key={product.itemId}>
                {product.name}
                {product.mode && (
                  <span className="coverage-extra-mode">
                    {' '}
                    · {t(`inventory.coverage.modes.${product.mode}`)}
                  </span>
                )}
              </li>
            ))}
          </ul>
        </details>
      )}

      {linking && (
        <ResaleLinkModal
          product={linking}
          catalogItems={catalogItems}
          onClose={() => setLinking(null)}
          onLinked={handleLinked}
        />
      )}
    </section>
  );
}

export default InventoryCoveragePanel;
